/**
 * FilterBuilder field list for the invoice list. Enum fields use
 * `input:'select'` so a condition folds into the backend base64 `filters`
 * object (`{ invoice_status: [...] }`), matching the customer/contract pattern.
 * The backend decodes `filters` as arbitrary JSON, so array-valued filters
 * (`product_group_name: string[]`) fold in via `input:'multiselect'`.
 *
 * `product_group_name` is a `multiselect` whose options are the live product
 * groups (see `buildInvoiceFilters`) — legacy's product-group async-select.
 *
 * Applied-date range lives OUTSIDE this field list: the backend wants a
 * 2-element `invoice_apply_startdate: [from, to]` array which FilterBuilder's
 * single-value inputs can't produce, so the invoice toolbar renders a
 * `TimeRangePicker` (@ui `/data`) and injects the range into `filters` directly.
 *
 * Still deferred:
 * - `account_manager_codes` — backend accepts it, but staging invoices carry no
 *   account-manager data, so the filter can't be verified to narrow correctly
 *   (code semantics: employee_id vs numeric employee_code unconfirmed). Wire once
 *   a record with AMs exists to probe against.
 */
import type { FilterField, FilterOption } from '@vnetwork/ui/data';
import { INVOICE_STATUS_OPTIONS, INVOICE_TYPE_OPTIONS } from '../invoice-format';
// Invoice rows carry the same `contract_role` / `contract_direction` enums as
// contracts — reuse the canonical option lists instead of re-declaring them.
import {
  CONTRACT_ROLE_OPTIONS,
  CONTRACT_DIRECTION_OPTIONS,
} from '@/pages/contracts/contract-format';

const statusFilter: FilterField = {
  value: 'invoice_status',
  label: 'Status',
  input: 'select',
  options: INVOICE_STATUS_OPTIONS,
};

const typeFilter: FilterField = {
  value: 'invoice_type',
  label: 'Type',
  input: 'select',
  options: INVOICE_TYPE_OPTIONS,
};

const roleFilter: FilterField = {
  value: 'contract_role',
  label: 'Role',
  input: 'select',
  options: CONTRACT_ROLE_OPTIONS,
};

const directionFilter: FilterField = {
  value: 'contract_direction',
  label: 'Direction',
  input: 'select',
  options: CONTRACT_DIRECTION_OPTIONS,
};

/** Invoice filters: status · type · role · direction. */
export const invoiceFilters: FilterField[] = [statusFilter, typeFilter, roleFilter, directionFilter];

/**
 * Full invoice filter set including the live product-group multiselect. Pass the
 * options from `useProductGroupOptions()`; when empty (still loading), the
 * product-group field is omitted so the picker never shows an empty option list.
 */
export function buildInvoiceFilters(productGroupOptions: FilterOption[]): FilterField[] {
  if (productGroupOptions.length === 0) return invoiceFilters;
  const productGroupFilter: FilterField = {
    value: 'product_group_name',
    label: 'Product group',
    input: 'multiselect',
    options: productGroupOptions,
  };
  return [statusFilter, typeFilter, productGroupFilter, roleFilter, directionFilter];
}
